import { useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";

// Types
import { IGetSellers } from "@/Washouts/types/PayrollClosing/queries";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";

// API
import { getRecipientCandidates } from "@/Washouts/api/PayrollClosing/queries";

// Hooks
import { useManagedPositions } from "@/Common/hooks/Positions";

// Materials
import { GridRowId, useGridApiContext } from "@mui/x-data-grid-premium";

type InputEditCellProps = {
	id: GridRowId;
	field: string;
	value?: IGetSellers;
	error?: boolean;
	locationId: string;
	positionsToFilter: string[];
};

export default function InputEditUserCell(props: Readonly<InputEditCellProps>) {
	const { id, field, value, error, locationId, positionsToFilter = [] } = props;

	const apiRef = useGridApiContext();
	const [dataValue, setDataValue] = useState<IGetSellers | null>(null);

	const { data } = useManagedPositions({
		queryOpts: {
			staleTime: Infinity,
		},
	});

	const positions = !positionsToFilter.length ? data : data?.filter((position) => positionsToFilter.includes(position));

	const {
		data: valuesData,
		isLoading,
		isFetching,
	} = useQuery({
		queryKey: ["adjustmentsRecipient", locationId],
		queryFn: async () => getRecipientCandidates(locationId, positions ?? []),
		staleTime: Infinity,
		enabled: !!positions?.length,
		placeholderData: [],
	});

	const handleChange = (changeValue: IGetSellers | null) => {
		if (changeValue) {
			// `debounceMs` is important to avoid the input to be updated too often.
			apiRef.current.setEditCellValue({ id, field, value: changeValue, debounceMs: 200 });
			setDataValue(changeValue);
		}
	};

	const getSortedOptions = (options: IGetSellers[] = []) =>
		options?.sort((item1: IGetSellers, item2: IGetSellers) => (item1.fullName > item2.fullName ? 1 : -1)) ?? [];

	useEffect(() => {
		if (value) {
			setDataValue(value);
		}
		return () => {
			setDataValue(null);
		};
	}, [value]);

	return (
		<SmartSelector
			selectBy={"_id"}
			showBy={"fullName"}
			options={getSortedOptions((valuesData as IGetSellers[]) ?? [])}
			selected={dataValue}
			inputProps={{
				placeholder: "Recipients",
				classes:
					"[&_.MuiInputBase-root]:!min-h-[26px] [&_.MuiInputBase-root]:!py-[3px] [&_.MuiInputBase-root_.MuiInputBase-input]:!rounded-none",
			}}
			listBoxProps={{
				sx: { maxHeight: "223px" },
			}}
			clearable
			disabled={isLoading}
			error={error || !dataValue}
			loading={isLoading || isFetching}
			onChangeAction={(selected) => handleChange(selected as IGetSellers)}
		/>
	);
}
